import CryptoJS from 'crypto-js';
import { env } from '../environment';
import { descifrar } from './tokenUtils';
import { ENCRYPTION } from '../constant/globalConstants';

const SECRET_KEY = env.llaveMaestra || env.cryptoKey;

export const encrypted = {
  // Encrypt a text using AES
  encrypt(text) {
    if (!text) return text;
    if (!ENCRYPTION) {
      return text;
    }
    try {
      const parsedBase64Key = CryptoJS.enc.Base64.parse(SECRET_KEY);
      const iv = CryptoJS.lib.WordArray.random(16);
      const encryptedData = CryptoJS.AES.encrypt(text, parsedBase64Key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      });
      // Prepend the IV to the encrypted text
      const ivAndCipherText = iv.concat(encryptedData.ciphertext).toString(CryptoJS.enc.Base64);
      return ivAndCipherText;
    } catch (e) {
      return text;
    }
  },

  // Decrypt a text using AES with default key or fallback
  decrypt(text) {
    if (!text) return text;
    try {
      const parsedBase64Key = CryptoJS.enc.Base64.parse(SECRET_KEY);
      const decodedText = CryptoJS.enc.Base64.parse(text);
      const iv = CryptoJS.lib.WordArray.create(decodedText.words.slice(0, 4));
      const cipherText = CryptoJS.lib.WordArray.create(decodedText.words.slice(4));
      const decryptedData = CryptoJS.AES.decrypt(
        { ciphertext: cipherText },
        parsedBase64Key,
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      );
      const decryptedText = decryptedData.toString(CryptoJS.enc.Utf8);
      if (decryptedText) return decryptedText;
    } catch (e) {
      // Ignore and fallback below
    }

    // Direct fallback if ENCRYPTION is false or CBC failed with SECRET_KEY
    const fallbackKey = env.llaveMaestra || env.cryptoKey;
    if (fallbackKey) {
      try {
        const decodedKey = descifrar(fallbackKey);
        const parsedBase64Key = CryptoJS.enc.Base64.parse(decodedKey);
        const decodedText = CryptoJS.enc.Base64.parse(text);
        const iv = CryptoJS.lib.WordArray.create(decodedText.words.slice(0, 4));
        const cipherText = CryptoJS.lib.WordArray.create(decodedText.words.slice(4));
        const decryptedData = CryptoJS.AES.decrypt(
          { ciphertext: cipherText },
          parsedBase64Key,
          {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
          }
        );
        const decryptedText = decryptedData.toString(CryptoJS.enc.Utf8);
        if (decryptedText) return decryptedText;
      } catch (e) {
        // Fallback failed
      }
    }

    return text;
  },

  // Helper function to decrypt with a custom key (supports CBC and GCM modes)
  async decryptWithKey(text, key) {
    let decrypted = null;

    // First attempt: CBC mode
    try {
      const parsedBase64Key = CryptoJS.enc.Base64.parse(key);
      const decodedText = CryptoJS.enc.Base64.parse(text);
      const iv = CryptoJS.lib.WordArray.create(decodedText.words.slice(0, 4));
      const cipherText = CryptoJS.lib.WordArray.create(decodedText.words.slice(4));
      const decryptedData = CryptoJS.AES.decrypt(
        { ciphertext: cipherText },
        parsedBase64Key,
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      );
      const result = decryptedData.toString(CryptoJS.enc.Utf8);
      if (result) {
        decrypted = result;
      }
    } catch {
      // Ignore CBC errors
    }

    // Second attempt: GCM mode
    if (!decrypted) {
      try {
        const rawKey = Uint8Array.from(atob(key), (c) => c.charCodeAt(0));
        const cryptoKey = await crypto.subtle.importKey('raw', rawKey, { name: 'AES-GCM' }, false, ['decrypt']);

        const combined = Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
        const nonce = combined.slice(0, 12);
        const cipherText = combined.slice(12);

        const decryptedBuffer = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: nonce },
          cryptoKey,
          cipherText
        );
        decrypted = new TextDecoder().decode(decryptedBuffer);
      } catch {
        // Ignore GCM errors
      }
    }

    if (!decrypted) {
      throw new Error('Decryption failed with custom key');
    }
    return decrypted;
  },
};

// Token management utilities (can be used in non-React contexts)
export const tokenManager = {
  getToken: (tokenKey = "token") => {
    try {
      const storedToken = localStorage.getItem(tokenKey);
      if (!storedToken) return null;
      try {
        const decrypted = encrypted.decrypt(storedToken);
        return decrypted || storedToken;
      } catch {
        return storedToken;
      }
    } catch (error) {
      console.error(`Error getting token ${tokenKey}:`, error);
      return null;
    }
  },

  setToken: (tokenValue, tokenKey = "token") => {
    try {
      if (!tokenValue) {
        localStorage.removeItem(tokenKey);
        return;
      }
      const tokenToStore = ENCRYPTION ? encrypted.encrypt(tokenValue) : tokenValue;
      localStorage.setItem(tokenKey, tokenToStore);
    } catch (error) {
      console.error(`Error storing token ${tokenKey}:`, error);
    }
  },

  removeToken: (tokenKey = "token") => {
    localStorage.removeItem(tokenKey);
  },
};

// Session ID management utilities
export const sessionManager = {
  getSessionId: (sessionKey = "sessionId") => {
    try {
      const sessionId = localStorage.getItem(sessionKey);
      return sessionId || null;
    } catch (error) {
      console.error(`Error getting sessionId ${sessionKey}:`, error);
      return null;
    }
  },

  setSessionId: (sessionIdValue, sessionKey = "sessionId") => {
    try {
      if (!sessionIdValue) {
        localStorage.removeItem(sessionKey);
        return;
      }
      localStorage.setItem(sessionKey, sessionIdValue);
    } catch (error) {
      console.error(`Error setting sessionId ${sessionKey}:`, error);
    }
  },

  removeSessionId: (sessionKey = "sessionId") => {
    localStorage.removeItem(sessionKey);
  },
};